/**
 * Manager for mesh creation and lifecycle
 */

import { type Mesh, MeshBuilder, type Scene, Vector3 } from "@babylonjs/core";
import { BaseManager } from "../core/BaseManager";
import type { MeshConfig } from "./types";

/**
 * Manages mesh creation and lifecycle
 */
export class MeshManager extends BaseManager {
  private scene: Scene | null = null;
  private meshes = new Map<string, Mesh>();

  /**
   * Implementation-specific initialization
   */
  protected async onInitialize(): Promise<void> {
    this.emit("meshManagerReady", {});
  }

  /**
   * Set the scene for mesh management
   * @param scene The scene to manage meshes in
   */
  setScene(scene: Scene): void {
    this.ensureInitialized();
    this.scene = scene;
  }

  /**
   * Create a mesh based on configuration
   * @param config Mesh configuration
   */
  createMesh(config: MeshConfig): Mesh {
    this.ensureInitialized();

    if (!this.scene) {
      throw new Error("Scene not set. Call setScene first.");
    }

    if (this.meshes.has(config.name)) {
      throw new Error(`Mesh with name ${config.name} already exists`);
    }

    let mesh: Mesh;

    switch (config.type) {
      case "box":
        mesh = MeshBuilder.CreateBox(
          config.name,
          {
            depth: config.depth,
            height: config.height,
            size: config.size ?? 1,
            width: config.width,
          },
          this.scene,
        );
        break;
      case "sphere":
        mesh = MeshBuilder.CreateSphere(
          config.name,
          {
            diameter: config.diameter ?? config.size ?? 1,
            segments: config.tessellation ?? 16,
          },
          this.scene,
        );
        break;
      case "ground":
        mesh = MeshBuilder.CreateGround(
          config.name,
          {
            height: config.height ?? 10,
            subdivisions: config.subdivisions ?? 1,
            width: config.width ?? 10,
          },
          this.scene,
        );
        break;
      case "cylinder":
        mesh = MeshBuilder.CreateCylinder(
          config.name,
          {
            diameter: config.diameter ?? 1,
            height: config.height ?? 2,
            tessellation: config.tessellation ?? 24,
          },
          this.scene,
        );
        break;
      case "torus":
        mesh = MeshBuilder.CreateTorus(
          config.name,
          {
            diameter: config.diameter ?? 1,
            tessellation: config.tessellation ?? 16,
            thickness: config.size ?? 0.3,
          },
          this.scene,
        );
        break;
      case "plane":
        mesh = MeshBuilder.CreatePlane(
          config.name,
          {
            height: config.height,
            size: config.size ?? 1,
            width: config.width,
          },
          this.scene,
        );
        break;
      default:
        throw new Error(`Unsupported mesh type: ${config.type}`);
    }

    // Apply transformations
    if (config.position) {
      mesh.position = new Vector3(
        config.position.x,
        config.position.y,
        config.position.z,
      );
    }
    if (config.rotation) {
      mesh.rotation = new Vector3(
        config.rotation.x,
        config.rotation.y,
        config.rotation.z,
      );
    }
    if (config.scaling) {
      mesh.scaling = new Vector3(
        config.scaling.x,
        config.scaling.y,
        config.scaling.z,
      );
    }

    // Store mesh
    this.meshes.set(config.name, mesh);

    // Emit event
    this.emit("meshCreated", {
      mesh,
      name: config.name,
      type: config.type,
    });

    return mesh;
  }

  /**
   * Get a mesh by name
   * @param name Mesh name
   */
  getMesh(name: string): Mesh | undefined {
    return this.meshes.get(name);
  }

  /**
   * Get all meshes
   */
  getAllMeshes(): Map<string, Mesh> {
    return new Map(this.meshes);
  }

  /**
   * Remove a mesh
   * @param name Mesh name
   */
  removeMesh(name: string): void {
    const mesh = this.meshes.get(name);
    if (!mesh) {
      return;
    }

    mesh.dispose();
    this.meshes.delete(name);

    this.emit("meshRemoved", { name });
  }

  /**
   * Clear all meshes
   */
  clearAll(): void {
    this.meshes.forEach((mesh) => {
      mesh.dispose();
    });
    this.meshes.clear();
  }

  /**
   * Implementation-specific disposal
   */
  protected onDispose(): void {
    this.clearAll();
    this.scene = null;
  }
}
